import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Camera as CameraIcon, Pencil } from "lucide-react";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { Section, SectionActionButton } from "@shared/ui/containers/Section";
import { Card } from "@shared/ui/containers/Card";
import { ListGroup } from "@shared/ui/lists/ListGroup";
import { ListItem } from "@shared/ui/lists/ListItem";
import { Skeleton, SkeletonRow } from "@shared/ui/feedback/Skeleton";
import { ErrorState } from "@shared/ui/feedback/ErrorState";
import { Button } from "@shared/ui/primitives/Button";
import { TextField } from "@shared/ui/primitives/TextField";
import { BottomSheet } from "@shared/ui/containers/BottomSheet";
import { useApiForm } from "@shared/hooks/useApiForm";
import { initialsOf, formatDate } from "@shared/lib/format";
import { cn } from "@shared/lib/cn";
import { platform } from "@shared/lib/platform";
import { toast } from "@shared/ui/feedback/Toast";
import { z } from "zod";
import { pageCopy } from "@shared/lib/pageCopy";
import { HeroHeader } from "@shared/ui/headers/HeroHeader";
import { ProfileBillingSections } from "./ProfileBillingSections";
import {
  fetchProfile,
  updateProfile,
  uploadProfileImage,
  type MemberProfile,
} from "./api";

/**
 * Profile screen. A dark estate header (back arrow, like the rest of the
 * app) over the resident's avatar, account balance, and three editable
 * iOS-grouped sections — Basics, Address, Emergency. Tapping a section's
 * edit affordance opens a bottom-sheet form.
 */
export function Profile(): JSX.Element {
  const copy = pageCopy().profile;
  const profileQuery = useQuery({
    queryKey: ["profile"],
    queryFn: fetchProfile,
  });

  return (
    <div className="min-h-full bg-paper">
      {/* ── Header ───────────────────────────────────────────────── */}
      <HeroHeader
        eyebrow={copy.eyebrow}
        title={copy.title}
        subtitle={copy.subtitle}
      />

      {/* ── Body ─────────────────────────────────────────────────── */}
      <div className="px-6 pb-safe-or-6">
        {profileQuery.isLoading ? (
          <div className="pt-6">
            <Skeleton className="h-24" />
            <div className="mt-6">
              <SkeletonRow />
            </div>
          </div>
        ) : profileQuery.isError || !profileQuery.data ? (
          <div className="pt-6">
            <ErrorState onRetry={() => profileQuery.refetch()} />
          </div>
        ) : (
          <ProfileBody profile={profileQuery.data} />
        )}
      </div>
    </div>
  );
}

function ProfileBody({ profile }: { profile: MemberProfile }): JSX.Element {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<
    "basics" | "address" | "emergency" | null
  >(null);

  const imageMutation = useMutation({
    mutationFn: uploadProfileImage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      toast.success("Profile photo updated");
    },
    onError: (e) =>
      toast.danger(
        "Couldn't upload photo",
        e instanceof Error ? e.message : undefined,
      ),
  });

  const onChangePhoto = async () => {
    try {
      let blob: Blob;
      if (platform.isNative()) {
        const photo = await Camera.getPhoto({
          quality: 75,
          resultType: CameraResultType.Uri,
          source: CameraSource.Prompt,
          allowEditing: true,
        });
        if (!photo.webPath) return;
        blob = await fetch(photo.webPath).then((r) => r.blob());
      } else {
        const file = await new Promise<File | null>((resolve) => {
          const input = document.createElement("input");
          input.type = "file";
          input.accept = "image/*";
          input.onchange = () => resolve(input.files?.[0] ?? null);
          input.click();
        });
        if (!file) return;
        blob = file;
      }
      imageMutation.mutate(blob);
    } catch (e) {
      console.warn("[profile] photo failed:", e);
    }
  };

  const displayName =
    [profile.firstName, profile.lastName].filter(Boolean).join(" ").trim() ||
    profile.email;

  return (
    <>
      <div className="flex flex-col items-center gap-3 pt-6">
        <button
          type="button"
          onClick={onChangePhoto}
          className="relative h-24 w-24 rounded-pill bg-primary-600 text-[color:var(--primary-ink)] flex items-center justify-center font-display text-display-lg overflow-hidden hairline"
          aria-label="Change profile photo"
        >
          {profile.profile_image_url ? (
            <img
              src={profile.profile_image_url}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            initialsOf(displayName, profile.email)
          )}
          <span className="absolute right-0 bottom-0 flex h-7 w-7 items-center justify-center rounded-pill bg-card hairline-strong shadow-elevated text-ink">
            <CameraIcon className="h-3.5 w-3.5" strokeWidth={1.75} />
          </span>
        </button>
        <div className="text-center">
          <p className="font-display text-display-lg text-ink">{displayName}</p>
          {profile.preferred_name ? (
            <p className="text-body-sm text-ink-muted">
              "{profile.preferred_name}"
            </p>
          ) : null}
          <p className="text-body-sm text-ink-secondary mt-0.5">
            {profile.email}
          </p>
        </div>
      </div>

      <Section
        title="Basics"
        action={
          <SectionActionButton onClick={() => setEditing("basics")}>
            <Pencil className="h-3 w-3" strokeWidth={1.75} />
            Edit
          </SectionActionButton>
        }
      >
        <ListGroup>
          <ListItem
            title="First name"
            trailing={
              <span className="text-body text-ink-secondary">
                {profile.firstName ?? "—"}
              </span>
            }
            noChevron
          />
          <ListItem
            title="Last name"
            trailing={
              <span className="text-body text-ink-secondary">
                {profile.lastName ?? "—"}
              </span>
            }
            noChevron
          />
          <ListItem
            title="Mobile"
            trailing={
              <span className="text-body text-ink-secondary">
                {profile.mobile ?? "—"}
              </span>
            }
            noChevron
          />
          <ListItem
            title="Date of birth"
            trailing={
              <span className="text-body text-ink-secondary">
                {formatDate(profile.date_of_birth) || "—"}
              </span>
            }
            noChevron
          />
        </ListGroup>
      </Section>

      <Section
        title="Address"
        action={
          <SectionActionButton onClick={() => setEditing("address")}>
            <Pencil className="h-3 w-3" strokeWidth={1.75} />
            Edit
          </SectionActionButton>
        }
      >
        <ListGroup>
          <ListItem
            title="Street"
            subtitle={
              [profile.address?.line1, profile.address?.line2]
                .filter(Boolean)
                .join(", ") || "—"
            }
            noChevron
          />
          <ListItem
            title="City"
            trailing={
              <span className="text-body text-ink-secondary">
                {profile.address?.city ?? "—"}
              </span>
            }
            noChevron
          />
          <ListItem
            title="Postal code"
            trailing={
              <span className="text-body text-ink-secondary">
                {profile.address?.postal_code ?? "—"}
              </span>
            }
            noChevron
          />
        </ListGroup>
      </Section>

      <Section
        title="Emergency contact"
        action={
          <SectionActionButton onClick={() => setEditing("emergency")}>
            <Pencil className="h-3 w-3" strokeWidth={1.75} />
            Edit
          </SectionActionButton>
        }
      >
        <ListGroup>
          <ListItem
            title="Name"
            trailing={
              <span className="text-body text-ink-secondary">
                {profile.emergency_contact?.name ?? "—"}
              </span>
            }
            noChevron
          />
          <ListItem
            title="Mobile"
            trailing={
              <span className="text-body text-ink-secondary">
                {profile.emergency_contact?.mobile ?? "—"}
              </span>
            }
            noChevron
          />
          <ListItem
            title="Relationship"
            trailing={
              <span className="text-body text-ink-secondary">
                {profile.emergency_contact?.relationship ?? "—"}
              </span>
            }
            noChevron
          />
        </ListGroup>
      </Section>

      <ProfileBillingSections />

      <Section title="Security & access">
        {(profile.auth_scopes ?? []).length > 0 ? (
          <div className="flex flex-col gap-2">
            {profile.auth_scopes!.map((scope) => (
              <Card key={scope.id} noGrain className="p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-display text-body text-ink">
                      {scope.code || "Scope"}
                    </p>
                    {scope.description ? (
                      <p className="mt-0.5 text-caption text-ink-muted">
                        {scope.description}
                      </p>
                    ) : null}
                  </div>
                  <span
                    className={cn(
                      "shrink-0 rounded-pill px-2 py-1 text-caption font-semibold",
                      scope.status === "active"
                        ? "bg-success/10 text-success"
                        : "bg-ink-muted/10 text-ink-muted",
                    )}
                  >
                    {scope.status}
                  </span>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <p className="text-body-sm text-ink-muted">
            No authentication scopes are assigned to this account.
          </p>
        )}
      </Section>

      {editing ? (
        <ProfileEditSheet
          section={editing}
          profile={profile}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </>
  );
}

const BasicsSchema = z.object({
  firstName: z.string().min(1, "Required"),
  lastName: z.string().min(1, "Required"),
  preferred_name: z.string().optional(),
  mobile: z.string().optional(),
});

const AddressSchema = z.object({
  address: z.object({
    line1: z.string().optional(),
    line2: z.string().optional(),
    city: z.string().optional(),
    region: z.string().optional(),
    postal_code: z.string().optional(),
    country: z.string().optional(),
  }),
});

const EmergencySchema = z.object({
  emergency_contact: z.object({
    name: z.string().optional(),
    mobile: z.string().optional(),
    relationship: z.string().optional(),
  }),
});

interface ProfileEditSheetProps {
  section: "basics" | "address" | "emergency";
  profile: MemberProfile;
  onClose: () => void;
}

function ProfileEditSheet({
  section,
  profile,
  onClose,
}: ProfileEditSheetProps): JSX.Element {
  const title =
    section === "basics"
      ? "Edit basics"
      : section === "address"
        ? "Edit address"
        : "Edit emergency contact";

  return (
    <BottomSheet
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={title}
    >
      {section === "basics" ? (
        <BasicsForm profile={profile} onClose={onClose} />
      ) : null}
      {section === "address" ? (
        <AddressForm profile={profile} onClose={onClose} />
      ) : null}
      {section === "emergency" ? (
        <EmergencyForm profile={profile} onClose={onClose} />
      ) : null}
    </BottomSheet>
  );
}

function BasicsForm({
  profile,
  onClose,
}: {
  profile: MemberProfile;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const form = useApiForm({
    schema: BasicsSchema,
    defaultValues: {
      firstName: profile.firstName ?? "",
      lastName: profile.lastName ?? "",
      preferred_name: profile.preferred_name ?? "",
      mobile: profile.mobile ?? "",
    },
  });
  const submit = form.handleSubmit(async (values) => {
    const result = await form.run(() => updateProfile(values));
    if (result) {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      toast.success("Profile updated");
      onClose();
    }
  });
  return (
    <form onSubmit={submit} className="flex flex-col gap-4 pb-2">
      <TextField
        label="First name"
        {...form.register("firstName")}
        error={form.formState.errors.firstName?.message}
      />
      <TextField
        label="Last name"
        {...form.register("lastName")}
        error={form.formState.errors.lastName?.message}
      />
      <TextField label="Preferred name" {...form.register("preferred_name")} />
      <TextField
        label="Mobile"
        type="tel"
        inputMode="tel"
        {...form.register("mobile")}
      />
      <FormButtons isPending={form.isPending} onCancel={onClose} />
    </form>
  );
}

function AddressForm({
  profile,
  onClose,
}: {
  profile: MemberProfile;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const form = useApiForm({
    schema: AddressSchema,
    defaultValues: { address: profile.address ?? {} },
  });
  const submit = form.handleSubmit(async (values) => {
    const result = await form.run(() => updateProfile(values));
    if (result) {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      toast.success("Address updated");
      onClose();
    }
  });
  return (
    <form onSubmit={submit} className="flex flex-col gap-4 pb-2">
      <TextField label="Street line 1" {...form.register("address.line1")} />
      <TextField label="Street line 2" {...form.register("address.line2")} />
      <TextField label="City" {...form.register("address.city")} />
      <TextField
        label="Postal code"
        {...form.register("address.postal_code")}
      />
      <TextField label="Country" {...form.register("address.country")} />
      <FormButtons isPending={form.isPending} onCancel={onClose} />
    </form>
  );
}

function EmergencyForm({
  profile,
  onClose,
}: {
  profile: MemberProfile;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const form = useApiForm({
    schema: EmergencySchema,
    defaultValues: { emergency_contact: profile.emergency_contact ?? {} },
  });
  const submit = form.handleSubmit(async (values) => {
    const result = await form.run(() => updateProfile(values));
    if (result) {
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      toast.success("Emergency contact updated");
      onClose();
    }
  });
  return (
    <form onSubmit={submit} className="flex flex-col gap-4 pb-2">
      <TextField
        label="Contact name"
        {...form.register("emergency_contact.name")}
      />
      <TextField
        label="Contact mobile"
        type="tel"
        inputMode="tel"
        {...form.register("emergency_contact.mobile")}
      />
      <TextField
        label="Relationship"
        {...form.register("emergency_contact.relationship")}
      />
      <FormButtons isPending={form.isPending} onCancel={onClose} />
    </form>
  );
}

function FormButtons({
  isPending,
  onCancel,
}: {
  isPending: boolean;
  onCancel: () => void;
}): JSX.Element {
  return (
    <div className="mt-2 flex gap-3">
      <Button
        variant="ghost"
        type="button"
        onClick={onCancel}
        className="flex-1"
      >
        Cancel
      </Button>
      <Button type="submit" loading={isPending} className="flex-1">
        Save
      </Button>
    </div>
  );
}
